<script setup>
import OverviewReportFilters from './OverviewReportFilters.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, h } from 'vue';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
import { useI18n } from 'vue-i18n';
import SlaQuebraAPI from 'dashboard/api/fandata/slaQuebra';
import BiraSummaryAPI from 'dashboard/api/fandata/biraSummary';

// A tela de Quebras chama este componente com `type` agent/team/inbox/label; a visão de conta
// inteira não tem quebra e por isso não pede SLA por linha.
const DIMENSOES_COM_SLA = {
  agent: 'agent',
  team: 'team',
  inbox: 'inbox',
  label: 'label',
};
import SummaryReportLink from './SummaryReportLink.vue';

const flagMap = {
  agent: 'isFetchingAgentSummaryReports',
  inbox: 'isFetchingInboxSummaryReports',
  team: 'isFetchingTeamSummaryReports',
  label: 'isFetchingLabelSummaryReports',
};

const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(() => uiFlags.value[flagMap[props.type]] ?? false);

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter([props.summaryKey]) || [];

const getMetrics = id =>
  reportMetrics.value.find(metrics => metrics.id === Number(id)) || {};
const columnHelper = createColumnHelper();
const { t } = useI18n();

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-n-slate-12',
    },
    cellProps.getValue()
  );

const columns = computed(() => [
  columnHelper.accessor('name', {
    header: t(`SUMMARY_REPORTS.${props.type.toUpperCase()}`),
    width: 300,
    cell: cellProps => h(SummaryReportLink, cellProps),
  }),
  columnHelper.accessor('conversationsCount', {
    header: t('SUMMARY_REPORTS.CONVERSATIONS'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgFirstResponseTime', {
    header: t('SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgResolutionTime', {
    header: t('SUMMARY_REPORTS.AVG_RESOLUTION_TIME'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgReplyTime', {
    header: t('SUMMARY_REPORTS.AVG_REPLY_TIME'),
    width: 200,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('resolutionsCount', {
    header: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
    width: 200,
    cell: defaulSpanRender,
  }),
  // T-255: o SLA saiu do menu e virou coluna aqui. O número é o do FanData (protocolo da
  // matriz do clube, primeiro contato e tratamento), buscado num endpoint nosso: o
  // `Reports::DataSource` do upstream serve todas as telas do produto e não conhece esse
  // conceito.
  columnHelper.accessor('slaNoPrazo', {
    header: t('SUMMARY_REPORTS.SLA_NO_PRAZO'),
    width: 200,
    cell: defaulSpanRender,
  }),
]);

// T-255. Chamada própria, e por isso o SLA pode aparecer um instante depois do resto da linha:
// é preferível a tabela abrir com o que já tem do que segurar tudo esperando um número.
//
// "--" quando a linha não tem conversa COM PROTOCOLO no período. Não é zero: zero diria que o
// SLA foi descumprido, quando o que houve é que não havia prazo a cumprir.
const slaPorLinha = ref({});

const buscarSla = async () => {
  // Mesmo motivo do `buscarBira`: janela em 1970 devolve coluna vazia.
  if (!from.value || !to.value) return;
  const dim = DIMENSOES_COM_SLA[props.type];
  if (!dim) return;
  try {
    const { data } = await SlaQuebraAPI.get({
      dim,
      since: from.value,
      until: to.value,
    });
    slaPorLinha.value = data?.linhas || {};
  } catch (e) {
    slaPorLinha.value = {};
  }
};

const renderSla = id => {
  const linha = slaPorLinha.value[id] ?? slaPorLinha.value[String(id)];
  if (!linha || linha.no_prazo === null || linha.no_prazo === undefined) return '--';
  return `${linha.no_prazo}%`;
};

// T-248 · O BIRA ENTRA COMO LINHA NA LISTA POR TIME.
//
// A tabela de times conta só o que passou por gente, e quem soma as linhas acha que está vendo a
// operação inteira. Falta exatamente o que a IA absorveu.
//
// ⚠️ Ele NÃO é um time no banco, e não vai virar. Criar um time chamado Bira faria a atribuição,
// o round-robin e as permissões passarem a enxergá-lo como fila de gente. Aqui é uma linha
// calculada, com a mesma forma das outras, e só na quebra por time: nas de agente, caixa e
// etiqueta ele não cabe.
const linhaDoBira = ref(null);

const buscarBira = async () => {
  if (props.type !== 'team') return;
  // ⚠️ `from`/`to` nascem em 0, e o primeiro fetch roda no onMounted, antes de o filtro de
  // período emitir os valores. Com 0 e 0 o servidor monta a janela em 1970, a linha volta toda
  // zerada, e se essa resposta chegar DEPOIS da real ela sobrescreve a boa: a linha do Bira
  // aparece só com traços. É o que explica o "atualmente não aparece" de 02/10.
  if (!from.value || !to.value) return;
  try {
    const { data } = await BiraSummaryAPI.get({
      since: from.value,
      until: to.value,
    });
    linhaDoBira.value = data || null;
  } catch (e) {
    linhaDoBira.value = null;
  }
};

const renderAvgTime = value => (value ? formatTime(value) : '--');

const renderCount = value => (value ? value.toLocaleString() : '--');

const tableDosTimes = computed(() =>
  rowItems.value.map(row => {
    const rowMetrics = getMetrics(row.id);
    const {
      conversationsCount,
      avgFirstResponseTime,
      avgResolutionTime,
      avgReplyTime,
      resolvedConversationsCount,
    } = rowMetrics;
    return {
      id: row.id,
      // we fallback on title, label for instance does not have a name property
      name: row.name ?? row.title,
      type: props.type,
      conversationsCount: renderCount(conversationsCount),
      avgFirstResponseTime: renderAvgTime(avgFirstResponseTime),
      avgReplyTime: renderAvgTime(avgReplyTime),
      avgResolutionTime: renderAvgTime(avgResolutionTime),
      resolutionsCount: renderCount(resolvedConversationsCount),
      slaNoPrazo: renderSla(row.id),
    };
  })
);

// A linha do Bira vai em PRIMEIRO, porque é por onde o atendimento entra: a leitura de cima para
// baixo passa a acompanhar o caminho do caso, e não a ordem alfabética dos times.
const tableData = computed(() => {
  const b = linhaDoBira.value;
  if (!b) return tableDosTimes.value;

  return [
    {
      id: b.id,
      name: b.name,
      type: props.type,
      // `semLink` desliga o link da primeira coluna: não existe página de time para o Bira, e um
      // link que leva a lugar nenhum é pior do que texto.
      semLink: true,
      conversationsCount: renderCount(b.conversations_count),
      avgFirstResponseTime: renderAvgTime(b.avg_first_response_time),
      avgReplyTime: renderAvgTime(b.avg_reply_time),
      avgResolutionTime: renderAvgTime(b.avg_resolution_time),
      resolutionsCount: renderCount(b.resolved_conversations_count),
      // O SLA por quebra é calculado por time; o Bira não tem linha lá, e inventar uma aqui
      // seria outro número com o mesmo nome.
      slaNoPrazo: '--',
    },
    ...tableDosTimes.value,
  ];
});

const fetchReportsWithRetry = async () => {
  buscarSla();
  buscarBira();
  const params = {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  };
  try {
    await store.dispatch(props.actionKey, params);
  } catch {
    try {
      await store.dispatch(props.actionKey, params);
    } catch {
      useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
    }
  }
};

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  fetchReportsWithRetry();
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

// downloadReports method is not used in this component
// but it is exposed to be used in the parent component
const downloadReports = () => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const fileName = generateFileName({
      type: props.type,
      to: to.value,
      businessHours: businessHours.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: businessHours.value,
    };
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <OverviewReportFilters
    :disabled="isLoading"
    @filter-change="onFilterChange"
  />
  <div
    class="relative flex-1 overflow-auto px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <Table :table="table" />
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isLoading"
        class="absolute inset-0 flex justify-center pt-[12.5rem] bg-n-solid-1/70 rounded-xl pointer-events-none"
      >
        <Spinner :size="32" class="text-n-brand" />
      </div>
    </Transition>
  </div>
</template>
